import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useSettings } from '@/contexts/SettingsContext'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  CheckIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { AgentProxyService } from '@/services/agentProxyService'

// Import TableColumn from compatibility types instead of defining locally
import type { TableColumn } from '@/types/compatibility'
import { RowEditModal } from './RowEditModal'
import { ConfirmDialog } from './ConfirmDialog'
import { FilterModal } from './FilterModal'
import { ExportModal, type ExportOptions } from './ExportModal'
import { ImportModal, type ImportOptions } from './ImportModal'
import { InlineEditCell } from './InlineEditCell'
import { ColumnFilter } from './ColumnFilter'
import { downloadData } from '@/utils/exportUtils'
import { toast } from 'react-hot-toast'
import { ContextMenu, type ContextMenuItem } from './ContextMenu'

interface TableDataViewProps {
  connection: DatabaseConnection
  database: string
  table: string
  schema?: string
  onEditStructure?: () => void
  isReadOnly?: boolean  // For views which are read-only
}

type TableRow = Record<string, unknown>

// Type for the table schema response
interface TableSchemaResponse {
  columns?: {
    name?: string
    column_name?: string
    type?: string
    data_type?: string
    dataType?: string
    nullable?: boolean
    default_value?: string
    defaultValue?: string
    primary_key?: boolean
    primaryKey?: boolean
    is_primary_key?: boolean
    isPrimaryKey?: boolean
    auto_increment?: boolean
    autoIncrement?: boolean
    is_auto_increment?: boolean
    isAutoIncrement?: boolean
    length?: number
    character_maximum_length?: number
    precision?: number
    numeric_precision?: number
    scale?: number
    numeric_scale?: number
  }[]
  primary_key?: {
    constrained_columns?: string[]
  }
  primaryKeys?: string[]
}

// Type for table data page response
interface TableDataPage {
  rows?: unknown[][]
  columns?: string[]
  hasNext?: boolean
  has_next?: boolean
  offset?: number
  totalCount?: number
  rowCount?: number
  pagination?: {
    page: number
    limit: number
    offset: number
    total_count: number
    total_pages?: number
    has_next: boolean
    has_previous: boolean
  }
}

export function TableDataView({ connection, database, table, schema, onEditStructure, isReadOnly = false }: TableDataViewProps) {
  const queryClient = useQueryClient()
  const { settings } = useSettings()
  const tableRef = useRef<HTMLDivElement | null>(null)
  
  // Use just the table name for all operations, pass schema as parameter
  const tableName = table
  
  // Stable empty object reference to prevent unnecessary re-renders
  const emptyInitialData = useMemo(() => ({}), [])
  
  // Create unique localStorage key for this table
  const storageKey = useMemo(() => {
    return `tableFilters_${connection.id}_${database}_${schema || 'default'}_${tableName}`
  }, [connection.id, database, schema, tableName])
  
  // Load saved filters from localStorage
  const loadSavedFilters = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved) as unknown
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          const data = parsed
          // Handle both old format (string) and new format (object with value and operator)
          let columnFilters: Record<string, { value: string; operator: string }> = {}
          if ('columnFilters' in data && typeof data.columnFilters === 'object' && data.columnFilters !== null && !Array.isArray(data.columnFilters)) {
            for (const [key, val] of Object.entries(data.columnFilters)) {
              if (typeof val === 'string') {
                // Old format: convert string to new format
                columnFilters[key] = { value: val, operator: '=' }
              } else if (typeof val === 'object' && val !== null && 'value' in val && 'operator' in val) {
                // New format
                const filterVal = val as { value?: unknown; operator?: unknown }
                columnFilters[key] = {
                  value: typeof filterVal.value === 'string' ? filterVal.value : '',
                  operator: typeof filterVal.operator === 'string' ? filterVal.operator : '='
                }
              }
            }
          }
          return {
            columnFilters,
            whereClause: 'whereClause' in data && typeof data.whereClause === 'string' ? data.whereClause : '',
            rawSqlInput: 'rawSqlInput' in data && typeof data.rawSqlInput === 'string' ? data.rawSqlInput : ''
          }
        }
      }
    } catch (error) {
      console.error('Failed to load saved filters:', error)
    }
    return {
      columnFilters: {} as Record<string, { value: string; operator: string }>,
      whereClause: '',
      rawSqlInput: ''
    }
  }, [storageKey])
  
  // Initialize state from localStorage
  const savedFilters = useMemo(() => loadSavedFilters(), [loadSavedFilters])
  
  // Infinite scroll and filtering state
  const [pageSize, setPageSize] = useState(settings.queryLimit || 100)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [whereClause, setWhereClause] = useState(savedFilters.whereClause)
  const [rawSqlInput, setRawSqlInput] = useState(savedFilters.rawSqlInput)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const [lastAppliedFromInput, setLastAppliedFromInput] = useState('')
  
  // Infinite scroll state - using react-query's infinite query instead
  
  // Auto-refresh state
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(null)
  
  // Refs for infinite scroll and auto-refresh
  const lastScrollTimeRef = useRef<number>(0)
  const fetchingRef = useRef<boolean>(false)
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  // Edit state
  const [editingRow, setEditingRow] = useState<TableRow | null>(null)
  const [deletingRowIndex, setDeletingRowIndex] = useState<number | null>(null)
  
  // Multi-selection state
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false })
  const [deletingRowIndices, setDeletingRowIndices] = useState<number[]>([])
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnName: string } | null>(null)
  const [editingValue, setEditingValue] = useState<unknown>(null)
  const [tempEditData, setTempEditData] = useState<Record<number, Record<string, unknown>>>({})
  
  // Column filter state
  const [columnFilters, setColumnFilters] = useState<Record<string, { value: string; operator: string }>>(savedFilters.columnFilters)
  const [isApplyingColumnFilters, setIsApplyingColumnFilters] = useState(false)

  // Build order by clause for sorting
  const orderBy = sortColumn ? `${sortColumn} ${sortDirection.toUpperCase()}` : undefined

  // Query for table structure to get column metadata
  const {
    data: tableDetails
  } = useQuery<TableSchemaResponse>({
    queryKey: ['table-structure', connection.id, database, tableName, schema],
    queryFn: async () => {
      const result = await AgentProxyService.getTableSchema(
        connection.id,
        database,
        tableName,
        schema
      )
      console.log('🔍 Raw getTableSchema response:', result)
      return result as TableSchemaResponse
    },
    enabled: !!(connection.id && database && tableName)
  })

  // Infinite query for table data
  const {
    data: infiniteQueryResult,
    isLoading: loading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage: queryHasNextPage,
    refetch: refetchData,
    error
  } = useInfiniteQuery<TableDataPage>({
    queryKey: ['table-data-infinite', connection.id, database, tableName, schema, pageSize, orderBy, whereClause],
    queryFn: async ({ pageParam = 1 }) => {
      const validPage = Math.max(1, Number(pageParam) || 1)
      const validPageSize = Math.max(1, pageSize)
      
      console.log('🚀 API Request Parameters:', {
        datasourceId: connection.id,
        databaseName: database,
        tableName: tableName,
        page: validPage,
        limit: validPageSize,
        schema: schema,
        connectionType: connection.type
      })
      
      const result = await AgentProxyService.getTableData(
        connection.id,
        database,
        tableName,
        {
          schemaName: schema,
          limit: validPageSize,
          page: validPage,
          where_clause: whereClause || undefined,
          order_by: orderBy || undefined,
          suppressToast: true
        }
      )
      
      return result as TableDataPage
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const apiHasNext = lastPage.hasNext || lastPage.has_next || lastPage.pagination?.has_next
      return apiHasNext ? allPages.length + 1 : undefined
    },
    staleTime: 0,
    retry: false,
    enabled: !!(connection.id && database && table)
  })

  // Extract and accumulate data from all pages
  const allPages = infiniteQueryResult?.pages || []
  const rawData: TableRow[] = allPages.flatMap((page) => {
    return page.rows?.map((row: unknown[], rowIndex: number) => {
      const rowObj: TableRow = {}
      
      // Handle both array and object row formats
      if (Array.isArray(row)) {
        // Array format: [value1, value2, ...]
        for (const [colIndex, col] of (page.columns || []).entries()) {
          rowObj[col] = row[colIndex]
        }
      } else if (typeof row === 'object') {
        // Object format: { col1: value1, col2: value2, ... }
        for (const key of Object.keys(row)) {
          rowObj[key] = (row as Record<string, unknown>)[key]
        }
      } else {
        console.warn(`Unexpected row format at index ${rowIndex}:`, row)
      }
      
      return rowObj
    }) || []
  })
  
  // Get metadata from the first page
  const firstPageData = allPages[0] as TableDataPage | undefined
  
  // Apply local search filter
  const filteredData = searchQuery.trim()
    ? rawData.filter(row => 
        Object.values(row).some(value => 
          (typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase())) || (typeof value === 'number' && String(value).includes(searchQuery))
        )
      )
    : rawData

  // Get columns from table details or fallback to response columns
  const columns: TableColumn[] = useMemo(() => tableDetails?.columns?.map((col) => ({
    name: col.name || col.column_name || '',
    type: col.type || col.data_type || 'VARCHAR',
    dataType: col.dataType || col.data_type || col.type || 'VARCHAR',  // Map various property names to dataType
    nullable: col.nullable !== undefined ? col.nullable : true,
    defaultValue: col.defaultValue || col.default_value,
    isPrimaryKey: col.isPrimaryKey || col.primary_key || col.is_primary_key,
    isAutoIncrement: col.isAutoIncrement || col.auto_increment || col.is_auto_increment,
    length: col.length || col.character_maximum_length || undefined,
    precision: col.precision || col.numeric_precision || undefined,
    scale: col.scale || col.numeric_scale || undefined
  })) ?? firstPageData?.columns?.map((col: string) => ({
    name: col,
    type: 'VARCHAR',
    dataType: 'VARCHAR',
    nullable: true
  })) ?? [], [tableDetails, firstPageData])

  // Extract primary keys from the API response format
  const primaryKeys: string[] = useMemo(() => {
    return tableDetails?.primary_key?.constrained_columns || 
      tableDetails?.primaryKeys || 
      tableDetails?.columns?.filter((col: { primary_key?: boolean; is_primary_key?: boolean }) => col.primary_key || col.is_primary_key).map((col: { name?: string; column_name?: string }) => col.name || col.column_name).filter((name): name is string => name !== undefined) || 
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      columns.filter(col => col.isPrimaryKey).map(col => col.name) || []
  }, [tableDetails, columns])

  // Infinite scroll logic
  const currentlyLoadedRows = rawData.length
  const lastPage = allPages[allPages.length - 1]
  const hasNext = lastPage?.hasNext || lastPage?.has_next || lastPage?.pagination?.has_next || false

  // Mutations for data operations
  const createRowMutation = useMutation({
    mutationFn: async (rowData: Record<string, unknown>) => {
      return await AgentProxyService.insertRows(
        connection.id,
        database,
        tableName,
        [rowData],
        { schemaName: schema }
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['table-data-infinite', connection.id, database, tableName, schema] })
      toast.success('Row created successfully')
    },
    onError: () => {
      // No toast notification for row creation errors
    }
  })

  const updateRowMutation = useMutation({
    mutationFn: async ({ rowId, rowData }: { rowId: string, rowData: Record<string, unknown> }) => {
      console.log('=== API CALL DEBUG ===')
      console.log('Making API call with rowId:', rowId)
      console.log('Making API call with rowData:', rowData)
      
      const payload = {
        datasourceId: connection.id,
        databaseName: database,
        tableName: tableName,
        rowId,
        requestBody: rowData,
        ...(schema && { schema: schema })
      }
      console.log('Final API payload:', payload)
      console.log('=== END API CALL DEBUG ===')
      
      // Create where conditions using primary key
      const whereConditions = primaryKeys.length > 0 
        ? { [primaryKeys[0]]: rowId }
        : {}

      return await AgentProxyService.updateRows(
        connection.id,
        database,
        tableName,
        rowData,
        whereConditions,
        { schemaName: schema }
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['table-data-infinite', connection.id, database, tableName, schema] })
      toast.success('Row updated successfully')
    },
    onError: () => {
      // No toast notification for row update errors
    }
  })

  const deleteRowMutation = useMutation({
    mutationFn: async ({ rowId, primaryKey }: { rowId: string | number, primaryKey: string }) => {
      // Create where condition using the actual primary key column name
      const whereCondition = { [primaryKey]: rowId }
      
      return await AgentProxyService.deleteRows(
        connection.id,
        database,
        tableName,
        whereCondition,
        { schemaName: schema }
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['table-data-infinite', connection.id, database, tableName, schema] })
      toast.success('Row deleted successfully')
    },
    onError: () => {
      // No toast notification for row delete errors
    }
  })

  const deleteMultipleRowsMutation = useMutation({
    mutationFn: async (rowIds: (string | number)[]) => {
      // Perform bulk delete by passing an array of IDs
      const [primaryKey] = primaryKeys
      if (!primaryKey) {
        throw new Error('No primary key found for bulk deletion')
      }
      
      // Pass an array of IDs to delete multiple rows at once
      // The backend should handle this as: DELETE FROM table WHERE id IN (value1, value2, ...)
      const whereCondition = {
        [primaryKey]: rowIds
      }
      
      return await AgentProxyService.deleteRows(
        connection.id,
        database,
        tableName,
        whereCondition,
        { schemaName: schema }
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['table-data-infinite', connection.id, database, tableName, schema] })
      toast.success(`${deletingRowIndices.length} rows deleted successfully`)
      setSelectedRows(new Set())
    },
    onError: () => {
      toast.error('Failed to delete rows')
    }
  })

  // Event handlers
  const handleRefresh = useCallback(() => {
    void refetchData()
  }, [refetchData])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    // This will trigger a refetch with the new page size
  }, [])

  const handleSort = useCallback((columnName: string) => {
    if (sortColumn === columnName) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(columnName)
      setSortDirection('asc')
    }
  }, [sortColumn, sortDirection])

  // Infinite scroll handler
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const now = Date.now()
    
    // Debounce scroll events to prevent too many calls
    if (now - lastScrollTimeRef.current < 300) {
      return
    }
    
    // Don't load more if already loading or no more pages
    if (fetchingRef.current || !queryHasNextPage) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
    
    // Trigger when scrolled 85% down
    if (scrollPercentage >= 0.85) {
      console.log('🔄 Scroll trigger - loading next page...')
      lastScrollTimeRef.current = now
      fetchingRef.current = true
      void fetchNextPage()
    }
  }, [queryHasNextPage, fetchNextPage])

  // Track fetching state
  useEffect(() => {
    fetchingRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  // Auto-refresh effect
  useEffect(() => {
    // Clear any existing timer
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current)
      autoRefreshTimerRef.current = null
    }

    // Set up new timer if interval is selected
    if (autoRefreshInterval && autoRefreshInterval > 0) {
      console.log(`🔄 Setting up auto-refresh with ${autoRefreshInterval}s interval`)
      
      autoRefreshTimerRef.current = setInterval(() => {
        // Don't refresh if currently fetching or editing
        if (fetchingRef.current || editingCell || showEditModal) {
          console.log('🔄 Skipping auto-refresh due to active operation')
          return
        }
        
        console.log('🔄 Auto-refresh triggered')
        
        // Save scroll position before refresh
        const scrollContainer = tableRef.current?.querySelector('.flex-1.overflow-auto')
        const scrollPosition = scrollContainer ? {
          top: scrollContainer.scrollTop,
          left: scrollContainer.scrollLeft
        } : null
        
        void refetchData().then(() => {
          // Restore scroll position after refresh
          if (scrollPosition && scrollContainer) {
            requestAnimationFrame(() => {
              scrollContainer.scrollTo(scrollPosition.left, scrollPosition.top)
            })
          }
          return undefined
        })
      }, autoRefreshInterval * 1000)
    }

    // Cleanup on unmount or interval change
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current)
        autoRefreshTimerRef.current = null
      }
    }
  }, [autoRefreshInterval, refetchData, editingCell, showEditModal])

  // Handle auto-refresh interval change
  const handleAutoRefreshChange = useCallback((interval: string) => {
    const newInterval = interval === 'off' ? null : Number.parseInt(interval)
    setAutoRefreshInterval(newInterval)
    
    if (newInterval) {
      // Trigger immediate refresh when enabling auto-refresh
      void refetchData()
    }
  }, [refetchData])

  // Row operations
  const handleAddRow = useCallback(() => {
    if (isReadOnly) return  // Prevent adding rows in read-only mode
    setEditingRow(null)
    setShowEditModal(true)
  }, [isReadOnly])

  const handleEditRow = useCallback((row: TableRow, _index: number) => {
    if (isReadOnly) return  // Prevent editing rows in read-only mode
    setEditingRow(row)
    setShowEditModal(true)
  }, [isReadOnly])

  const handleSaveRow = useCallback(async (rowData: Record<string, unknown>) => {
    console.log('=== SAVE ROW DEBUG ===')
    console.log('Received rowData:', rowData)
    console.log('Current editingRow:', editingRow)
    console.log('Is editing mode:', !!editingRow)
    
    if (editingRow) {
      // Update existing row
      const [primaryKey] = primaryKeys
      if (primaryKey && editingRow[primaryKey]) {
        console.log('Updating row with ID:', editingRow[primaryKey])
        console.log('Sending update payload:', rowData)
        await updateRowMutation.mutateAsync({ 
          rowId: editingRow[primaryKey] as string, 
          rowData 
        })
      } else {
        throw new Error('No primary key found for row update')
      }
    } else {
      // Create new row
      console.log('Creating new row with data:', rowData)
      await createRowMutation.mutateAsync(rowData)
    }
    console.log('=== END SAVE ROW DEBUG ===')
  }, [editingRow, primaryKeys, updateRowMutation, createRowMutation])

  const handleConfirmDelete = useCallback(async () => {
    // Handle single row deletion
    if (deletingRowIndex !== null) {
      const row = filteredData[deletingRowIndex]
      const [primaryKey] = primaryKeys
      if (primaryKey && (row[primaryKey] !== null && row[primaryKey] !== undefined)) {
        await deleteRowMutation.mutateAsync({ 
          rowId: row[primaryKey] as string | number, 
          primaryKey 
        })
      } else {
        throw new Error('No primary key found for row deletion')
      }
      setDeletingRowIndex(null)
    }
    // Handle multiple rows deletion
    else if (deletingRowIndices.length > 0) {
      const [primaryKey] = primaryKeys
      if (!primaryKey) {
        throw new Error('No primary key found for row deletion')
      }
      const rowIds = deletingRowIndices
        .map(index => filteredData[index]?.[primaryKey] as string | number)
        .filter(id => id !== null && id !== undefined)
      if (rowIds.length > 0) {
        await deleteMultipleRowsMutation.mutateAsync(rowIds)
      }
      setDeletingRowIndices([])
    }
    setShowDeleteConfirm(false)
  }, [deletingRowIndex, deletingRowIndices, filteredData, primaryKeys, deleteRowMutation, deleteMultipleRowsMutation])

  const handleApplyFilter = useCallback((whereClause: string) => {
    setWhereClause(whereClause)
    setRawSqlInput(whereClause)
    setLastAppliedFromInput('') // Reset so external updates can sync properly
    // Clear column filters when applying from FilterModal
    setColumnFilters({})
    // This will trigger a refetch with the new filter
  }, [])

  // Convert column filters to SQL WHERE clause
  const buildWhereClauseFromFilters = useCallback(() => {
    const conditions: string[] = []
    
    for (const [columnName, filter] of Object.entries(columnFilters)) {
      if (!filter.value && filter.operator !== 'IS NULL' && filter.operator !== 'IS NOT NULL') {
        continue
      }
      
      const column = columns.find(c => c.name === columnName)
      if (!column) continue
      
      const dataType = column.dataType?.toLowerCase() || ''
      const isNumeric = dataType.includes('int') || dataType.includes('decimal') || 
                       dataType.includes('float') || dataType.includes('double') || 
                       dataType.includes('numeric') || dataType.includes('real')
      const isBool = dataType.includes('bool')
      
      let condition = ''
      
      // Escape single quotes in values for SQL safety
      const escapedValue = filter.value.replace(/'/g, "''")
      
      // Use appropriate quoting based on database type
      // PostgreSQL uses double quotes, MySQL uses backticks
      const isPostgreSQL = connection.type === 'postgresql'
      const quotedColumnName = isPostgreSQL ? `"${columnName}"` : `\`${columnName}\``
      
      if (filter.operator === 'IS NULL' || filter.operator === 'IS NOT NULL') {
        condition = `${quotedColumnName} ${filter.operator}`
      } else if (filter.operator === 'LIKE' || filter.operator === 'NOT LIKE') {
        condition = `${quotedColumnName} ${filter.operator} '%${escapedValue}%'`
      } else if (isNumeric || isBool) {
        condition = `${quotedColumnName} ${filter.operator} ${filter.value}`
      } else {
        condition = `${quotedColumnName} ${filter.operator} '${escapedValue}'`
      }
      
      conditions.push(condition)
    }
    
    return conditions.join(' AND ')
  }, [columnFilters, columns, connection.type])

  // Handle column filter change
  const handleColumnFilterChange = useCallback((columnName: string, value: string, operator: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnName]: { value, operator }
    }))
    setIsApplyingColumnFilters(true)
  }, [])

  // Handle column filter clear
  const handleColumnFilterClear = useCallback((columnName: string) => {
    setColumnFilters(prev => {
      const { [columnName]: _, ...rest } = prev
      return rest
    })
    setIsApplyingColumnFilters(true)
  }, [])

  // Apply column filters when they change
  useEffect(() => {
    if (isApplyingColumnFilters) {
      const filterClause = buildWhereClauseFromFilters()
      setWhereClause(filterClause)
      setRawSqlInput(filterClause)
      setLastAppliedFromInput('')
      setIsApplyingColumnFilters(false)
    }
  }, [columnFilters, isApplyingColumnFilters, buildWhereClauseFromFilters])

  const handleExportData = useCallback(async (options: ExportOptions): Promise<void> => {
    try {
      // Fetch all data for export (or limited data based on options)
      const exportResult = await AgentProxyService.getTableData(
        connection.id,
        database,
        tableName,
        {
          schemaName: schema,
          limit: options.limit || 10000,
          page: 1,
          suppressToast: true
        }
      )

      const exportResult_typed = exportResult as { rows?: unknown[][]; columns?: string[] }
      const exportData = exportResult_typed.rows?.map((row: unknown[]) => {
        const rowObj: Record<string, unknown> = {}
        for (const [colIndex, col] of (exportResult_typed.columns ?? []).entries()) {
          rowObj[col] = Array.isArray(row) ? row[colIndex] : row[col]
        }
        return rowObj
      }) ?? []

      const filename = `${database}_${table}_${new Date().toISOString().split('T')[0]}`
      downloadData(exportData, options, filename)
    } catch (error) {
      // No toast notification for export errors
      console.error('Export failed:', error)
    }
  }, [connection.id, database, tableName, schema, table])

  const handleImportData = useCallback(async (options: ImportOptions): Promise<void> => {
    try {
      const nonce = crypto.randomUUID()
      const timestamp = Date.now() / 1000
      
      const result = await AgentProxyService.importData(
        connection.id,
        database,
        {
          table_name: tableName,
          schema_name: schema || 'public',
          file_data: options.fileData,
          file_format: options.fileFormat,
          has_header: options.hasHeader,
          delimiter: options.delimiter,
          nonce,
          timestamp
        }
      ) as { status?: 'success' | 'error'; rows_imported?: number; message?: string }
      
      console.log('Import result:', result)
      
      if (result?.status === 'success') {
        // Show success message with row count
        const successMessage = result.message || `Successfully imported ${result.rows_imported || 0} rows`
        toast.success(successMessage)
        
        // Close the import modal
        setShowImportModal(false)
        
        // Refresh the table data - invalidate all queries that start with this prefix
        await queryClient.invalidateQueries({
          queryKey: ['table-data-infinite', connection.id, database, tableName, schema],
          exact: false  // This will match any query that starts with this key
        })
        // Force refetch
        await refetchData()
        // Reset page data to trigger re-fetch
        await queryClient.resetQueries({
          queryKey: ['table-data-infinite', connection.id, database, tableName, schema],
          exact: false
        })
      } else {
        toast.error(result?.message || 'Import failed')
      }
    } catch (error) {
      console.error('Import failed:', error)
      toast.error('Failed to import data. Please check the file format and try again.')
    }
  }, [connection.id, database, tableName, schema, queryClient, refetchData])

  // Inline editing handlers
  const startInlineEdit = useCallback((rowIndex: number, columnName: string, currentValue: unknown) => {
    if (isReadOnly) return  // Prevent inline editing in read-only mode
    setEditingCell({ rowIndex, columnName })
    setEditingValue(currentValue)
  }, [isReadOnly])

  const cancelInlineEdit = useCallback(() => {
    setEditingCell(null)
    setEditingValue(null)
  }, [])

  const saveInlineEdit = useCallback(async (valueToSave?: unknown) => {
    if (!editingCell) return

    const { rowIndex, columnName } = editingCell
    const row = filteredData[rowIndex]
    const [primaryKey] = primaryKeys

    if (!primaryKey || row[primaryKey] === null || row[primaryKey] === undefined) {
      console.log('🚫 Primary Key Error Debug:', {
        primaryKey,
        primaryKeys,
        row,
        'row[primaryKey]': row[primaryKey],
        'Object.keys(row)': Object.keys(row),
        tableDetails
      })
      console.error('Cannot update row: Primary key not found')
      return
    }

    // Use provided value or current editing value
    const newValue = valueToSave !== undefined ? valueToSave : editingValue

    // Check if value actually changed (with proper type normalization)
    const originalValue = row[columnName]
    
    // Normalize values for comparison
    const normalizeValue = (val: unknown) => {
      if (val === null || val === undefined) return null
      if (val === '') return null  // Treat empty string as null for comparison
      return val
    }
    
    const normalizedNew = normalizeValue(newValue)
    const normalizedOriginal = normalizeValue(originalValue)
    
    // Deep comparison for complex values
    const hasChanged = JSON.stringify(normalizedNew) !== JSON.stringify(normalizedOriginal)
    
    if (!hasChanged) {
      console.log('🚫 No actual change detected:', { originalValue, newValue })
      // No change, just close the editor
      setEditingCell(null)
      setEditingValue(null)
      return
    }

    try {
      // Update temp data first for immediate UI feedback
      setTempEditData(prev => ({
        ...prev,
        [rowIndex]: {
          ...prev[rowIndex],
          [columnName]: newValue
        }
      }))

      // Prepare payload with only changed field + primary key for identification
      const changePayload: Record<string, unknown> = {
        [primaryKey]: row[primaryKey], // Include primary key for row identification
        [columnName]: newValue         // Only the changed field
      }

      console.log('🔄 Inline edit - sending payload:', {
        originalValue,
        newValue,
        payload: changePayload
      })

      // Update via API with minimal payload
      await updateRowMutation.mutateAsync({
        rowId: row[primaryKey] as string,
        rowData: changePayload
      })

      setEditingCell(null)
      setEditingValue(null)
    } catch (error) {
      // Remove from temp data on error
      setTempEditData(prev => {
        const newData = { ...prev }
        const rowData = newData[rowIndex]
        // Remove data for this cell
        const updatedRow = { ...rowData }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [columnName]: _, ...rest } = updatedRow
        if (Object.keys(rest).length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [rowIndex]: __, ...newDataRest } = newData
          return newDataRest
        } else {
          newData[rowIndex] = rest
        }
        return newData
      })
      console.error('Failed to update cell:', error)
    }
  }, [editingCell, filteredData, primaryKeys, updateRowMutation, editingValue, tableDetails])

  const discardRowChanges = useCallback((rowIndex: number) => {
    setTempEditData(prev => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [rowIndex]: _, ...newData } = prev
      return newData
    })
    if (editingCell?.rowIndex === rowIndex) {
      cancelInlineEdit()
    }
  }, [editingCell, cancelInlineEdit])

  const saveRowChanges = useCallback(async (rowIndex: number) => {
    const row = filteredData[rowIndex]
    const changes = tempEditData[rowIndex]
    const [primaryKey] = primaryKeys

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!changes || !primaryKey || row[primaryKey] === null || row[primaryKey] === undefined) {
      return
    }

    try {
      const updatedRow = { ...row, ...changes }
      await updateRowMutation.mutateAsync({
        rowId: row[primaryKey] as string,
        rowData: updatedRow
      })

      // Clear temp data for this row
      setTempEditData(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [rowIndex]: _, ...newData } = prev
        return newData
      })
    } catch (error) {
      console.error('Failed to save row changes:', error)
    }
  }, [filteredData, tempEditData, primaryKeys, updateRowMutation])

  const getCellValue = (row: TableRow, rowIndex: number, columnName: string) => {
    // Return temp value if cell is being edited
    const rowTempData = tempEditData[rowIndex]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (rowTempData && columnName in rowTempData) {
      return rowTempData[columnName]
    }
    return row[columnName]
  }

  const formatCellValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 dark:text-gray-500 italic font-mono text-xs">NULL</span>
    }
    if (typeof value === 'boolean') {
      return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
          value 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {value ? 'TRUE' : 'FALSE'}
        </span>
      )
    }
    if (typeof value === 'number') {
      return <span className="font-mono text-right tabular-nums text-xs">{value.toLocaleString()}</span>
    }
    
    // Handle PostgreSQL arrays
    if (Array.isArray(value)) {
      return (
        <span className="font-mono text-xs" title={JSON.stringify(value)}>
          [{value.map(v => typeof v === 'string' ? `"${v}"` : String(v)).join(', ')}]
        </span>
      )
    }
    
    // Handle JSON objects (PostgreSQL JSON/JSONB)
    if (typeof value === 'object') {
      return (
        <span className="font-mono text-xs" title={JSON.stringify(value, null, 2)}>
          {JSON.stringify(value).substring(0, 50)}...
        </span>
      )
    }
    
    if (typeof value === 'string' && (/^\d{4}-\d{2}-\d{2}/.exec(value)) && value.includes('T')) {
      // Looks like a datetime
      try {
        const date = new Date(value)
        return <span className="font-mono text-xs">{date.toLocaleString()}</span>
      } catch {
        return <span className="break-all text-xs">{value}</span>
      }
    }
    if (typeof value === 'string' && (/^\d{4}-\d{2}-\d{2}$/.exec(value))) {
      // Looks like a date
      return <span className="font-mono text-xs">{value}</span>
    }
    
    // Handle PostgreSQL UUID format
    if (typeof value === 'string' && (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.exec(value))) {
      return <span className="font-mono text-xs text-purple-600 dark:text-purple-400" title="UUID">{value}</span>
    }
    
    if (typeof value === 'string' && value.length > 40) {
      // Long text - show preview (reduced from 50 to 40 for smaller screens)
      return (
        <span className="break-all text-xs" title={value}>
          {value.substring(0, 40)}...
        </span>
      )
    }
    return <span className="break-all text-xs">{typeof value === 'string' ? value : ''}</span>
  }

  const renderInlineCell = (row: TableRow, rowIndex: number, column: TableColumn) => {
    const columnName = column.name
    const isCurrentlyEditing = editingCell !== null && editingCell.rowIndex === rowIndex && editingCell.columnName === columnName
    const cellValue = getCellValue(row, rowIndex, columnName)
    const isPrimaryKey = primaryKeys.includes(columnName)
    const rowTempData = tempEditData[rowIndex]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const hasChanges = rowTempData && (columnName in rowTempData)

    if (isCurrentlyEditing) {
      return (
        <div className="w-full">
          <InlineEditCell
            value={editingValue}
            column={column}
            onSave={handleInlineEditSave}
            onCancel={cancelInlineEdit}
          />
        </div>
      )
    }

    // Render display cell
    return (
      <div
        className={`w-full h-full flex items-center px-2 py-1 rounded-md transition-all duration-150 group/cell ${
          isPrimaryKey 
            ? 'bg-blue-50 dark:bg-blue-900/20 cursor-not-allowed' 
            : (isReadOnly 
                ? 'cursor-default hover:bg-gray-50 dark:hover:bg-gray-700/50'  // Read-only styling
                : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-sm')
        } ${
          hasChanges 
            ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700' 
            : 'border border-transparent'
        }`}
        onDoubleClick={createInlineCellDoubleClickHandler(rowIndex, columnName, cellValue, isPrimaryKey)}
        title={isPrimaryKey ? 'Primary key (read-only)' : (isReadOnly ? 'View data is read-only' : 'Double-click to edit • Press Enter to save, Escape to cancel')}
      >
        <div className="flex-1 min-w-0 flex items-center">
          <span className="truncate text-xs leading-4">
            {formatCellValue(cellValue)}
          </span>
          {!isPrimaryKey && !isReadOnly && (  // Hide pencil icon in read-only mode
            <span className="ml-1 opacity-0 group-hover/cell:opacity-100 text-xs text-gray-400 dark:text-gray-500 transition-opacity">
              ✏️
            </span>
          )}
          {isPrimaryKey && (
            <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
              🔑
            </span>
          )}
          {hasChanges && (
            <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">
              ●
            </span>
          )}
        </div>
      </div>
    )
  }

  // Sync rawSqlInput with whereClause when it changes from external sources (like Advanced Filter modal)
  // Only sync if the change didn't come from the user applying their own input
  useEffect(() => {
    if (!isUserTyping && whereClause !== lastAppliedFromInput) {
      setRawSqlInput(whereClause)
    }
  }, [whereClause, isUserTyping, lastAppliedFromInput])

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const dataToSave = {
      columnFilters,
      whereClause,
      rawSqlInput
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(dataToSave))
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error)
    }
  }, [columnFilters, whereClause, rawSqlInput, storageKey])
  
  // Reset sort state when switching tables (but load saved filters for new table)
  useEffect(() => {
    setSortColumn(null)
    setSortDirection('asc')
    setIsUserTyping(false)
    
    // Load saved filters for the new table
    const newSavedFilters = loadSavedFilters()
    setColumnFilters(newSavedFilters.columnFilters)
    setWhereClause(newSavedFilters.whereClause)
    setRawSqlInput(newSavedFilters.rawSqlInput)
    setLastAppliedFromInput('')
  }, [connection.id, database, tableName, schema, loadSavedFilters])

  // Memoized callback functions to prevent unnecessary re-renders
  const handleShowFilterModal = useCallback(() => setShowFilterModal(true), [])
  const handleShowExportModal = useCallback(() => setShowExportModal(true), [])
  const handleShowImportModal = useCallback(() => setShowImportModal(true), [])
  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false)
    setEditingRow(null)
  }, [])
  const handleCloseDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false)
    setDeletingRowIndex(null)
  }, [])
  const handleCloseFilterModal = useCallback(() => setShowFilterModal(false), [])
  const handleCloseExportModal = useCallback(() => setShowExportModal(false), [])
  const handleCloseImportModal = useCallback(() => setShowImportModal(false), [])
  const handleInlineEditSave = useCallback((value: unknown) => {
    console.log('📥 Received value from InlineEditCell:', value)
    setEditingValue(value)
    // Use the value directly instead of relying on state
    void saveInlineEdit(value)
  }, [saveInlineEdit])
  const handleConfirmDeleteMemoized = useCallback(() => {
    void handleConfirmDelete()
  }, [handleConfirmDelete])
  const handleExportDataMemoized = useCallback((options: ExportOptions) => {
    void handleExportData(options)
  }, [handleExportData])
  const handleImportDataMemoized = useCallback(async (options: ImportOptions) => {
    return handleImportData(options)
  }, [handleImportData])

  // Selection handlers
  const handleRowClick = useCallback((e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    
    if (e.shiftKey && lastSelectedIndex !== null) {
      // Range selection with Shift
      const start = Math.min(lastSelectedIndex, index)
      const end = Math.max(lastSelectedIndex, index)
      const newSelection = new Set(selectedRows)
      for (let i = start; i <= end; i++) {
        newSelection.add(i)
      }
      setSelectedRows(newSelection)
    } else if (e.ctrlKey || e.metaKey) {
      // Toggle selection with Ctrl/Cmd
      const newSelection = new Set(selectedRows)
      if (newSelection.has(index)) {
        newSelection.delete(index)
      } else {
        newSelection.add(index)
      }
      setSelectedRows(newSelection)
      setLastSelectedIndex(index)
    } else {
      // Single selection
      setSelectedRows(new Set([index]))
      setLastSelectedIndex(index)
    }
  }, [selectedRows, lastSelectedIndex])

  const handleRowContextMenu = useCallback((e: React.MouseEvent, index: number) => {
    e.preventDefault()
    
    // If right-clicking on unselected row, select only that row
    if (!selectedRows.has(index)) {
      setSelectedRows(new Set([index]))
      setLastSelectedIndex(index)
    }
    
    setContextMenu({ x: e.clientX, y: e.clientY, visible: true })
  }, [selectedRows])

  const handleCopyRows = useCallback((format: 'csv' | 'insert') => {
    const selectedData = Array.from(selectedRows).map(index => filteredData[index])
    
    if (format === 'csv') {
      // Convert to CSV format
      const headers = columns.map(col => col.name).join(',')
      const rows = selectedData.map(row => 
        columns.map(col => {
          const value = row[col.name]
          if (value === null || value === undefined) return ''
          if (typeof value === 'string' && value.includes(',')) return `"${value.replace(/"/g, '""')}"`
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
          return JSON.stringify(value)
        }).join(',')
      ).join('\n')
      const csv = `${headers}\n${rows}`
      
      void navigator.clipboard.writeText(csv)
        .then(() => toast.success(`Copied ${selectedRows.size} rows as CSV`))
        .catch(() => toast.error('Failed to copy to clipboard'))
    } else if (format === 'insert') {
      // Convert to INSERT statements
      const statements = selectedData.map(row => {
        const columnNames = columns.map(col => `\`${col.name}\``).join(', ')
        const values = columns.map(col => {
          const value = row[col.name]
          if (value === null || value === undefined) return 'NULL'
          if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`
          if (typeof value === 'boolean') return value ? '1' : '0'
          if (typeof value === 'number') return String(value)
          return JSON.stringify(value)
        }).join(', ')
        return `INSERT INTO \`${database}\`.\`${table}\` (${columnNames}) VALUES (${values});`
      }).join('\n')
      
      void navigator.clipboard.writeText(statements)
        .then(() => toast.success(`Copied ${selectedRows.size} rows as INSERT statements`))
        .catch(() => toast.error('Failed to copy to clipboard'))
    }
    
    setContextMenu({ ...contextMenu, visible: false })
  }, [selectedRows, filteredData, columns, database, table, contextMenu])

  const handleDeleteSelectedRows = useCallback(() => {
    setDeletingRowIndices(Array.from(selectedRows))
    setShowDeleteConfirm(true)
    setContextMenu({ ...contextMenu, visible: false })
  }, [selectedRows, contextMenu])

  // Handle edit for single selected row
  const handleEditSelectedRow = useCallback(() => {
    if (selectedRows.size === 1) {
      const [index] = Array.from(selectedRows)
      const row = filteredData[index]
      if (row) {
        handleEditRow(row, index)
        setContextMenu({ ...contextMenu, visible: false })
      }
    }
  }, [selectedRows, filteredData, handleEditRow, contextMenu])

  // Create context menu items
  const contextMenuItems = useMemo<ContextMenuItem[]>(() => {
    const count = selectedRows.size
    if (count === 0) return []
    
    const items: ContextMenuItem[] = []
    
    // Add Edit option only when single row is selected AND not read-only
    if (count === 1 && !isReadOnly) {
      items.push({
        id: 'edit',
        label: 'Edit row',
        icon: PencilIcon,
        onClick: handleEditSelectedRow
      })
      items.push({
        id: 'separator-edit',
        label: '',
        separator: true,
        onClick: () => {}
      })
    }
    
    // Copy options (always available, even in read-only mode)
    items.push(
      {
        id: 'copy-csv',
        label: `Copy ${count} row${count > 1 ? 's' : ''} as CSV`,
        icon: DocumentDuplicateIcon,
        onClick: () => { handleCopyRows('csv') }
      },
      {
        id: 'copy-insert',
        label: `Copy ${count} row${count > 1 ? 's' : ''} as INSERT`,
        icon: CodeBracketIcon,
        onClick: () => { handleCopyRows('insert') }
      }
    )
    
    // Add delete option only if not read-only
    if (!isReadOnly) {
      items.push(
        {
          id: 'separator-delete',
          label: '',
          separator: true,
          onClick: () => {}
        },
        {
          id: 'delete',
          label: `Delete ${count} row${count > 1 ? 's' : ''}`,
          icon: TrashIcon,
          onClick: handleDeleteSelectedRows,
          variant: 'danger'
        }
      )
    }
    
    return items
  }, [selectedRows, handleCopyRows, handleDeleteSelectedRows, handleEditSelectedRow, isReadOnly])

  // Create memoized handlers for table column clicks
  const createColumnClickHandler = useCallback((columnName: string) => {
    return () => { handleSort(columnName) }
  }, [handleSort])

  // Memoized callback for input change events
  const handleSearchQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  // Memoized callback for page size change
  const handlePageSizeSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    handlePageSizeChange(Number(e.target.value))
  }, [handlePageSizeChange])

  // Memoized callback for auto refresh change
  const handleAutoRefreshSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    handleAutoRefreshChange(e.target.value)
  }, [handleAutoRefreshChange])

  // Memoized callback for inline cell editing (now with double-click)
  const createInlineCellDoubleClickHandler = useCallback((rowIndex: number, columnName: string, cellValue: unknown, isPrimaryKey: boolean) => {
    return (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!isPrimaryKey && !isReadOnly) {  // Prevent editing in read-only mode
        startInlineEdit(rowIndex, columnName, cellValue)
      }
    }
  }, [isReadOnly, startInlineEdit])

  // Clear selection when clicking outside
  const handleTableClick = useCallback((e: React.MouseEvent) => {
    const { target } = e
    if (target instanceof Element && typeof target.closest === 'function' && !target.closest('tr')) {
      setSelectedRows(new Set())
    }
  }, [])

  // Reset selection when data changes
  useEffect(() => {
    setSelectedRows(new Set())
    setLastSelectedIndex(null)
  }, [connection.id, database, tableName, schema])

  return (
    <div className="h-full bg-white dark:bg-gray-800 flex flex-col" ref={tableRef}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1 min-w-0 mr-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {isReadOnly ? 'View Data' : 'Table Data'}: {table}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
              {database}{schema ? `.${schema}` : ''}.{table}
              {isReadOnly && <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">(Read-only)</span>}
            </p>
          </div>
          
          <div className="flex items-center space-x-1.5 flex-shrink-0">
            {/* Page Size Dropdown */}
            <select
              value={pageSize}
              onChange={handlePageSizeSelectChange}
              className="border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors"
            >
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
              <option value={200}>200 rows</option>
              <option value={500}>500 rows</option>
            </select>
            
            {/* Auto-refresh dropdown */}
            <div className="relative">
              <select
                value={autoRefreshInterval || 'off'}
                onChange={handleAutoRefreshSelectChange}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                title="Auto-refresh interval"
              >
                <option value="off">Auto-refresh: Off</option>
                <option value="1">Auto-refresh: 1s</option>
                <option value="3">Auto-refresh: 3s</option>
                <option value="5">Auto-refresh: 5s</option>
                <option value="10">Auto-refresh: 10s</option>
                <option value="30">Auto-refresh: 30s</option>
              </select>
              {autoRefreshInterval && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Auto-refresh active"></div>
              )}
            </div>
            
            <button type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50 transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {onEditStructure && !isReadOnly && (
              <button type="button" 
                onClick={onEditStructure}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors flex items-center gap-1 whitespace-nowrap" 
                title="Edit Table Structure"
              >
                <PencilIcon className="w-3 h-3" />
                <span>Edit Structure</span>
              </button>
            )}
            {!isReadOnly && (
              <button type="button" 
                onClick={handleShowImportModal}
                className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
                title="Import"
              >
                <DocumentArrowUpIcon className="w-3 h-3" />
              </button>
            )}
            <button type="button" 
              onClick={handleShowExportModal}
              className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
              title="Export"
            >
              <DocumentArrowDownIcon className="w-3 h-3" />
            </button>
            {!isReadOnly && (
              <button type="button" 
                onClick={handleAddRow}
                className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1 whitespace-nowrap"
              >
                <PlusIcon className="w-3 h-3" />
                <span>Add Row</span>
              </button>
            )}
          </div>
        </div>

        {/* Status Display */}
        <div className="flex items-center justify-between mb-4">
          <div></div>

          {/* Infinite Scroll Status */}
          <div className="flex items-center space-x-3">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {currentlyLoadedRows.toLocaleString()} of {currentlyLoadedRows.toLocaleString()}{hasNext ? '+' : ''} rows
            </span>
            {isFetchingNextPage && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                <span>Loading more...</span>
              </div>
            )}
          </div>
        </div>

        {/* Advanced SQL Filter Section */}
        <div className="mb-0">
          <div className="flex items-start space-x-3">
            {/* Query Builder Button */}
            <button type="button" 
              onClick={handleShowFilterModal}
              className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors flex items-center gap-1 whitespace-nowrap" 
              title="Advanced Filter - Query Builder"
            >
              <FunnelIcon className="w-3 h-3" />
              <span>Query Builder</span>
            </button>
            
            <div className="flex-1 relative">
              <textarea
                placeholder=""
                value={typeof rawSqlInput === 'string' ? rawSqlInput : ''}
                onChange={(e) => {
                  setRawSqlInput(e.target.value)
                  setIsUserTyping(true)
                }}
                onKeyDown={(e) => {
                  const input = typeof rawSqlInput === 'string' ? rawSqlInput : String(rawSqlInput ?? '')
                  if ((e.key === 'Enter' && e.ctrlKey) || (e.key === 'Enter' && !e.shiftKey && input.trim() && !input.includes('\n'))) {
                    e.preventDefault()
                    setWhereClause(input)
                    setLastAppliedFromInput(input)
                    setIsUserTyping(false)
                    // Clear column filters when manually entering SQL
                    if (input !== buildWhereClauseFromFilters()) {
                      setColumnFilters({})
                    }
                  }
                }}
                onBlur={() => { setIsUserTyping(false) }}
                className={`w-full px-2 py-1.5 border rounded-md text-xs font-mono focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors resize-y min-h-[32px] max-h-[120px] ${
                  rawSqlInput ? 'pr-16' : 'pr-2'
                } ${
                  whereClause 
                    ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100' 
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                } placeholder-gray-500 dark:placeholder-gray-400`}
                title="Enter raw SQL WHERE clause (without the WHERE keyword). Press Ctrl+Enter to apply quickly"
                rows={1}
              />
              
              {/* Clear button inside textarea */}
              {rawSqlInput && (
                <button
                  type="button"
                  onClick={() => {
                    setRawSqlInput('')
                    setWhereClause('')
                    setLastAppliedFromInput('')
                    setIsUserTyping(false)
                    setColumnFilters({})
                    // Clear from localStorage as well
                    try {
                      localStorage.removeItem(storageKey)
                    } catch (error) {
                      console.error('Failed to clear saved filters:', error)
                    }
                  }}
                  className="absolute top-1.5 right-2 px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors z-10"
                  title="Clear filter"
                >
                  ×
                </button>
              )}
              
              {/* Active indicator */}
              {whereClause && (
                <div className="absolute top-1 right-8">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    Active
                  </span>
                </div>
              )}
            </div>
            
            {/* Apply button next to textarea */}
            <div className="flex items-start">
              <button
                type="button"
                onClick={() => {
                  const input = typeof rawSqlInput === 'string' ? rawSqlInput : String(rawSqlInput ?? '')
                  setWhereClause(input)
                  setLastAppliedFromInput(input)
                  setIsUserTyping(false)
                  // Clear column filters when manually entering SQL
                  if (input !== buildWhereClauseFromFilters()) {
                    setColumnFilters({})
                  }
                }}
                disabled={rawSqlInput === whereClause}
                className="px-2 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded transition-colors h-[32px]"
                title="Apply filter"
              >
                Apply
              </button>
            </div>
            
            {/* Search Field */}
            <div className="relative flex-shrink-0">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-gray-500 z-10" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchQueryChange}
                className="pl-7 pr-3 py-1.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded text-xs w-40 sm:w-48 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors h-[32px]"
                title="Search through visible data"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
        {error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Failed to load table data: {error.message}
            </p>
            {connection.type === 'postgresql' && schema && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                📝 Tip: If you're having trouble accessing PostgreSQL table data, ensure the schema "{schema}" exists and you have the necessary permissions.
              </p>
            )}
            <button onClick={handleRefresh} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" type="button">
              Try Again
            </button>
          </div>
        ) : loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading data...</p>
          </div>
        ) : (
          <div className="h-full overflow-auto" onScroll={handleScroll}>
            <table className="min-w-full w-max divide-y divide-gray-200 dark:divide-gray-700 table-fixed border-b border-gray-200 dark:border-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-20">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-16 border-r border-gray-200 dark:border-gray-600">
                    <div className="space-y-1">
                      <div className="flex items-center justify-center">
                        #
                      </div>
                      <div className="h-[20px] flex items-center justify-center">
                        {(Object.keys(columnFilters).length > 0 || whereClause) && (
                          <button
                            type="button"
                            onClick={() => {
                              setColumnFilters({})
                              setWhereClause('')
                              setRawSqlInput('')
                              setLastAppliedFromInput('')
                              setIsUserTyping(false)
                              // Clear from localStorage as well
                              try {
                                localStorage.removeItem(storageKey)
                              } catch (error) {
                                console.error('Failed to clear saved filters:', error)
                              }
                            }}
                            className="group px-1.5 py-0.5 text-[9px] bg-gray-100 hover:bg-red-100 dark:bg-gray-700 dark:hover:bg-red-900/20 text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 rounded transition-all flex items-center gap-0.5 border border-gray-300 hover:border-red-300 dark:border-gray-600 dark:hover:border-red-600"
                            title={`Clear ${Object.keys(columnFilters).length || 1} filter${Object.keys(columnFilters).length !== 1 ? 's' : ''}`}
                          >
                            <XMarkIcon className="w-2.5 h-2.5" />
                            <span>Clear</span>
                            {Object.keys(columnFilters).length > 0 && (
                              <span className="ml-0.5 px-1 bg-gray-200 dark:bg-gray-600 group-hover:bg-red-200 dark:group-hover:bg-red-800/30 rounded text-[8px] font-semibold">
                                {Object.keys(columnFilters).length}
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </th>
                  {columns.map((column) => {
                    const isPrimaryKey = primaryKeys.includes(column.name) || column.isPrimaryKey || false
                    return (
                      <th
                        key={column.name}
                        className={`px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 last:border-r-0 min-w-[100px] ${
                          isPrimaryKey ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                        }`}
                      >
                        <div className="space-y-1">
                          <div 
                            className="flex items-center space-x-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1 py-0.5 -mx-1 transition-colors"
                            onClick={column.name ? createColumnClickHandler(column.name) : undefined}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-1">
                                <span className="truncate font-medium text-xs">{column.name}</span>
                                {isPrimaryKey && (
                                  <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-blue-500 text-white whitespace-nowrap">
                                    PK
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 normal-case truncate">
                                {column.dataType || 'unknown'}
                              </div>
                            </div>
                            {sortColumn === column.name && (
                              <span className="text-blue-600 dark:text-blue-400 flex-shrink-0 text-xs">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                          {/* Filter input integrated into header */}
                          <div className="px-1">
                            {column.name && (
                              <ColumnFilter
                                column={column}
                                value={columnFilters[column.name]?.value || ''}
                                onChange={(value, operator) => { handleColumnFilterChange(column.name, value, operator) }}
                                onClear={() => { handleColumnFilterClear(column.name) }}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" onClick={handleTableClick}>
                {filteredData.map((row, index) => {
                  const rowNumber = index + 1 // Simple sequential numbering for infinite scroll
                  const rowTempData = tempEditData[index]
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  const hasChanges = rowTempData && (Object.keys(rowTempData).length > 0)
                  const isSelected = selectedRows.has(index)
                  
                  return (
                    <tr 
                      key={index} 
                      className={`group transition-colors h-10 ${
                        isSelected 
                          ? 'bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-800/40' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      } ${
                        hasChanges ? 'bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-400' : ''
                      } cursor-pointer`}
                      onClick={(e) => { handleRowClick(e, index) }}
                      onContextMenu={(e) => { handleRowContextMenu(e, index) }}
                    >
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-600 text-center font-mono">
                        <div className="flex items-center justify-center h-6">
                          {rowNumber}
                        </div>
                      </td>
                      {columns.map((column) => (
                        <td
                          key={column.name}
                          className="px-2 py-1.5 text-xs text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600 last:border-r-0 align-top"
                        >
                          <div className="h-6 flex items-center">
                            {column.name && renderInlineCell(row, index, column)}
                          </div>
                        </td>
                      ))}
                      {hasChanges && (
                        <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500 text-center">
                          <div className="flex items-center justify-center space-x-1 h-6">
                            <button type="button"
                              onClick={() => void saveRowChanges(index)}
                              disabled={updateRowMutation.isPending}
                              className="inline-flex items-center justify-center w-6 h-6 text-green-600 hover:text-green-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors disabled:opacity-50"
                              title="Save changes"
                            >
                              <CheckIcon className="w-3 h-3" />
                            </button>
                            <button type="button"
                              onClick={() => { discardRowChanges(index) }}
                              className="inline-flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                              title="Discard changes"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
            
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {filteredData.length === 0 && !loading && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <div className="py-12 text-center">
                  <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-full h-full">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-3.5m-9 0h3.5m0 0V8a2 2 0 012-2h3a2 2 0 012 2v5.01" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">No data found</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'Try adjusting your search criteria' : 'This table appears to be empty'}
                  </p>
                </div>
              </div>
            )}
            
            {/* Infinite Scroll Loading Footer */}
            {filteredData.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <div className="py-4 text-center">
                  {isFetchingNextPage ? (
                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                      <span>Loading more rows...</span>
                    </div>
                  ) : queryHasNextPage ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Scroll down to load more rows
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ✓ All rows loaded ({currentlyLoadedRows.toLocaleString()} rows)
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <RowEditModal
        key={editingRow ? `edit-${JSON.stringify(editingRow)}` : 'add-new'}
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSave={handleSaveRow}
        columns={columns}
        initialData={editingRow || emptyInitialData}
        isEditing={!!editingRow}
        primaryKeys={primaryKeys}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDeleteMemoized}
        title={deletingRowIndices.length > 1 ? `Delete ${deletingRowIndices.length} Rows` : "Delete Row"}
        message={deletingRowIndices.length > 1 
          ? `Are you sure you want to delete ${deletingRowIndices.length} rows? This action cannot be undone.`
          : "Are you sure you want to delete this row? This action cannot be undone."}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        isLoading={deleteRowMutation.isPending || deleteMultipleRowsMutation.isPending}
      />

      <FilterModal
        isOpen={showFilterModal}
        onClose={handleCloseFilterModal}
        onApply={handleApplyFilter}
        columns={columns}
        initialFilter={typeof whereClause === 'string' ? whereClause : ''}
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={handleCloseExportModal}
        onExport={handleExportDataMemoized}
        columns={columns}
        totalRows={currentlyLoadedRows}
      />

      <ImportModal
        isOpen={showImportModal}
        onClose={handleCloseImportModal}
        onImport={handleImportDataMemoized}
        tableName={tableName}
        databaseName={database}
        schemaName={schema}
      />

      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        visible={contextMenu.visible}
        items={contextMenuItems}
        onClose={() => { setContextMenu({ ...contextMenu, visible: false }) }}
      />
    </div>
  )
}
